import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { parseComponentAST, extractComponentBlock } from 'ast/componentParser';

// Global registry to track executed components code
let lastComponentsCode: string | null = null;

// Component to inject components.js globally (once, or when code changes)
export const ComponentScriptInjector = () => {
	const designSystemContent = useSelector((r: RootReducer) => r.mode.designSystemContent);
	const scriptRef = useRef<HTMLScriptElement | null>(null);

	useEffect(() => {
		if (
			!designSystemContent?.rawCode?.components ||
			designSystemContent.rawCode.components.trim().length === 0
		) {
			return;
		}

		const componentsCode = designSystemContent.rawCode.components;

		// Only execute if the code has changed
		if (lastComponentsCode === componentsCode) {
			return;
		}

		// Remove old script if it exists
		const existingScript = document.getElementById('design-system-components') as HTMLScriptElement;
		if (existingScript) {
			existingScript.remove();
		}

		// Parse components.js using AST
		const parsed = parseComponentAST(componentsCode);

		// Build modified code that skips already-defined components
		let modifiedCode = '';
		let lastIndex = 0;

		for (const block of parsed.blocks) {
			// Add code before this block
			modifiedCode += componentsCode.substring(lastIndex, block.classStartIndex);

			// Check if this custom element is already defined
			const isDefined = customElements.get(block.tagName) !== undefined;
			// Check if the class already exists in global scope
			const classExists = typeof (window as any)[block.className] !== 'undefined';

			// Only include the block if:
			// 1. Custom element is not registered (first time), OR
			// 2. Class doesn't exist (safe to declare)
			// Skip if both are defined (prevents redeclaration errors)
			if (!isDefined || !classExists) {
				// Include the full component block (class + define)
				modifiedCode += extractComponentBlock(block, componentsCode);
			}

			lastIndex = block.defineEndIndex;
		}

		// Add remaining code after the last component
		modifiedCode += componentsCode.substring(lastIndex);

		// Only execute if we have valid blocks and code
		if (parsed.blocks.length === 0 && modifiedCode.trim().length > 0) {
			console.warn('No component blocks found, skipping script execution. Code may be malformed.');
			return;
		}

		// Validate syntax before execution to prevent crashes
		// Use a simple check: try to parse with Function constructor (safer than eval)
		let canExecute = true;
		if (modifiedCode.trim().length > 0) {
			try {
				// This will throw if there's a syntax error
				new Function(modifiedCode);
			} catch (syntaxError) {
				console.warn('Component code contains syntax errors, skipping execution:', syntaxError);
				canExecute = false;
			}
		}

		// Execute the modified script only if syntax is valid
		if (canExecute && modifiedCode.trim().length > 0) {
			try {
				const script = document.createElement('script');
				script.type = 'text/javascript';
				script.id = 'design-system-components';
				script.textContent = modifiedCode;

				document.head.appendChild(script);
				scriptRef.current = script;
			} catch (error) {
				console.error('Failed to inject component script:', error);
			}
		}

		lastComponentsCode = componentsCode;

		return () => {
			// Don't remove script on unmount - keep it for other components
			// Components are registered globally and should persist
		};
	}, [designSystemContent?.rawCode?.components]);

	return null;
};
